import type { Videos } from '../api/videos.js'
import {
  captureScreen,
  ScreenCaptureError,
  supportsScreenCapture,
  type CapturedScreen,
} from '../media/screen-capture.js'
import { captureFailureMessage } from '../media/screen-share.js'
import {
  COMMUNICATIONS_DEVICE_ID,
  DeviceList,
  loadDevicePrefs,
  saveDevicePref,
  type DevicePrefs,
} from '../media/devices.js'
import type { KeyValueStore } from '../storage.js'
import type { UploaderProgress } from '../recording/uploader.js'
import { derived, patch, readonly, writable, type ReadableStore } from '../store.js'
import {
  BUBBLE_SIZES,
  bubbleAtCorner,
  cornerOf,
  DEFAULT_BUBBLE,
  hitsBubble,
  moveBubbleTo,
  resizeBubble,
  type BubbleCorner,
  type BubbleSpec,
} from './bubble.js'
import {
  CaptureEngine,
  type CaptureHooks,
  type CaptureSources,
  type CompositorStats,
} from './engine.js'
import { captureProfile, type CaptureMode, type CaptureProfile } from './profile.js'

/**
 * Recording a video, the client's side: what to record, the devices, the
 * bubble, the countdown, the take, and the upload. It outlives any page —
 * a person who navigates away while the last parts go up does not lose
 * them — and owns the tracks; an engine is one take over those tracks,
 * made when the countdown ends and made again for a restart. Nothing is
 * composited before that: a page shows the camera live in the bubble over
 * a sketch of the frame, not a picture of the screen, which on the screen
 * would show itself.
 */

export type CapturePhase =
  'setup' | 'countdown' | 'recording' | 'paused' | 'uploading' | 'done' | 'error'

export interface CaptureState {
  phase: CapturePhase
  mode: CaptureMode
  screen: CapturedScreen | null
  camera: MediaStream | null
  mic: MediaStreamTrack | null
  bubble: BubbleSpec
  title: string
  countdown: number
  elapsedMs: number
  /**
   * The longest a take may run, if anything: it stops there and is saved.
   * None by default; a plan may set one (setLimit).
   */
  limitMs: number | null
  progress: UploaderProgress | null
  /** What stopped the take, or the upload. */
  error: string | null
  /** A device that would not start; the screen is still recordable. */
  deviceError: string | null
  result: { videoId: string; shareToken: string } | null
  /** The compositor's own account of itself, once a second while recording. */
  health: CompositorStats | null
  /** The MIME type this take records with. */
  codec: string | null
  /** The bubble is painted into this take and may move; otherwise the screen is recorded as it is. */
  compositing: boolean
}

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err))

/**
 * A moment for the page to paint what changed and for the screen to show it: two frames, then a
 * little more. A hidden or covered page paints no frames, so it is also given up on after a short
 * while.
 */
export function letThePageSettle(): Promise<void> {
  const frames = new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame !== 'function') return resolve()
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  })
  const waited = Promise.race([frames, new Promise<void>((r) => setTimeout(r, 400))])
  return waited.then(() => new Promise<void>((r) => setTimeout(r, 250)))
}

function describeDeviceError(err: unknown, what: 'camera' | 'microphone'): string {
  const name = err instanceof DOMException ? err.name : ''
  switch (name) {
    case 'NotAllowedError':
      return `The ${what} was refused. Allow it in the address bar to record with it.`
    case 'NotFoundError':
    case 'OverconstrainedError':
      return `No ${what} was found.`
    case 'NotReadableError':
      return `The ${what} is in use by another app.`
    default:
      return `The ${what} could not be started: ${message(err)}`
  }
}

export class Capture {
  private readonly store = writable<CaptureState>({
    phase: 'setup',
    mode: 'screen',
    screen: null,
    camera: null,
    mic: null,
    bubble: { ...DEFAULT_BUBBLE },
    title: '',
    countdown: 0,
    elapsedMs: 0,
    limitMs: null,
    progress: null,
    error: null,
    deviceError: null,
    result: null,
    health: null,
    codec: null,
    compositing: false,
  })
  readonly state: ReadableStore<CaptureState> = readonly(this.store)
  /** This browser can record a video at all. */
  readonly supported = typeof window !== 'undefined' && CaptureEngine.supported()
  /** This browser has a screen picker (phones, mostly, do not). */
  readonly canPickScreen = supportsScreenCapture()
  readonly canStart: ReadableStore<boolean>
  readonly running: ReadableStore<boolean>
  /** The frame the take is (or would be) recorded in; the sketch and the bubble follow it. */
  readonly frame: ReadableStore<CaptureProfile>
  /** The camera goes in a bubble over the screen (not "camera only"). */
  readonly showsBubble: ReadableStore<boolean>
  /** A bubble is on offer: a screen take, with or without the camera on now. It is what the "show my camera" switch is for. */
  readonly offersBubble: ReadableStore<boolean>
  /** The corner the bubble is nearest. */
  readonly bubbleCorner: ReadableStore<BubbleCorner>
  /** Which of BUBBLE_SIZES the bubble is nearest. */
  readonly bubbleSizeIndex: ReadableStore<number>
  /** The microphones and cameras to choose from, kept up to date as they are plugged in. */
  readonly devices = new DeviceList()
  /** The microphone in use, read back from its track; null with none. */
  readonly activeMicId: ReadableStore<string | null>
  /** The camera in use, read back from its track; null with none. */
  readonly activeCameraId: ReadableStore<string | null>

  private engine: CaptureEngine | null = null
  private ticker: ReturnType<typeof setInterval> | null = null
  private countdownTimer: ReturnType<typeof setInterval> | null = null
  private pendingStop: Promise<void> | null = null
  private unfollow: (() => void) | null = null
  /** The devices last chosen, here or in a call: the same choice serves both. */
  private prefs: DevicePrefs

  constructor(
    private readonly videos: Videos,
    private readonly storage: KeyValueStore | null = null,
  ) {
    this.prefs = loadDevicePrefs(storage)
    this.canStart = derived([this.store], (s) =>
      s.mode === 'screen' ? s.screen !== null : s.camera !== null,
    )
    this.running = derived([this.store], (s) => s.phase === 'recording' || s.phase === 'paused')
    this.showsBubble = derived([this.store], (s) => s.mode === 'screen' && s.camera !== null)
    this.offersBubble = derived([this.store], (s) => s.mode === 'screen')
    const bubble = derived([this.store], (s) => s.bubble)
    this.bubbleCorner = derived([bubble], (b) => cornerOf(b))
    this.bubbleSizeIndex = derived([bubble], (b) =>
      BUBBLE_SIZES.reduce(
        (best, size, i) =>
          Math.abs(size - b.size) < Math.abs((BUBBLE_SIZES[best] ?? 0) - b.size) ? i : best,
        0,
      ),
    )
    this.activeMicId = derived([this.store], (s) => s.mic?.getSettings().deviceId ?? null)
    this.activeCameraId = derived(
      [this.store],
      (s) => s.camera?.getVideoTracks()[0]?.getSettings().deviceId ?? null,
    )
    const mode = derived([this.store], (s) => s.mode)
    const screen = derived([this.store], (s) => s.screen)
    const camera = derived([this.store], (s) => s.camera)
    // Recorded at the source's own size: the screen's capture, or the camera's picture.
    this.frame = derived([mode, screen, camera], () => {
      const s = this.store.get()
      const settings =
        s.mode === 'camera'
          ? s.camera?.getVideoTracks()[0]?.getSettings()
          : s.screen?.video.getSettings()
      return captureProfile({
        mode: s.mode,
        sourceWidth: settings?.width ?? null,
        sourceHeight: settings?.height ?? null,
      })
    })
  }

  private get s(): CaptureState {
    return this.store.get()
  }

  // ------------------------------------------------------------ sources

  /** Camera and microphone, asked for on arrival; a refusal still leaves the screen recordable. */
  async init(): Promise<void> {
    if (!this.supported) return
    patch(this.store, { deviceError: null })
    if (!this.s.camera) await this.enableCamera()
    if (!this.s.mic) await this.enableMic()
    // Names and ids are whole only once a device has been allowed.
    await this.devices.refresh()
    this.unfollow ??= this.devices.follow(() => undefined)
  }

  /**
   * The browser's picker: tabs and windows. `entireScreen` offers the whole screen too, which
   * records everything on it, the browser's own sharing bar and the recorder's floating controls
   * included (see captureScreen).
   */
  async pickScreen(opts: { entireScreen?: boolean } = {}): Promise<void> {
    if (this.s.phase !== 'setup') return
    patch(this.store, { error: null })
    try {
      const got = await captureScreen(opts)
      if (!got) return
      this.dropScreen()
      got.video.addEventListener('ended', this.onScreenEnded, { once: true })
      patch(this.store, { screen: got, mode: 'screen' })
    } catch (err) {
      patch(this.store, {
        error:
          err instanceof ScreenCaptureError
            ? (captureFailureMessage(err.failure) ?? 'The screen could not be captured.')
            : message(err),
      })
    }
  }

  /** The browser's own "Stop sharing" bar: a take in progress is finished, a choice undone. */
  private readonly onScreenEnded = (): void => {
    if (this.running.get()) {
      void this.stop()
      return
    }
    this.dropScreen()
  }

  dropScreen(): void {
    const current = this.s.screen
    if (!current) return
    current.video.removeEventListener('ended', this.onScreenEnded)
    for (const track of current.stream.getTracks()) track.stop()
    patch(this.store, { screen: null })
  }

  /**
   * The camera: the one asked for, else the one chosen last time (here or
   * in a call) if it is still plugged in, else the browser's.
   */
  private async enableCamera(deviceId?: string): Promise<boolean> {
    if (this.s.camera) return true
    const want = deviceId ?? this.prefs.camera
    try {
      const camera = await navigator.mediaDevices.getUserMedia({
        video: {
          ...(want ? { deviceId: deviceId ? { exact: want } : { ideal: want } } : {}),
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
        },
      })
      patch(this.store, { camera })
      return true
    } catch (err) {
      patch(this.store, { deviceError: describeDeviceError(err, 'camera') })
      return false
    }
  }

  /** Stop the camera (its light goes out) and hide the bubble, which has nothing left to show. */
  private disableCamera(): void {
    for (const track of this.s.camera?.getTracks() ?? []) track.stop()
    patch(this.store, { camera: null })
    if (this.s.bubble.visible) this.setBubble({ ...this.s.bubble, visible: false })
  }

  /**
   * The microphone, chosen as the camera is; with no choice, Windows' own
   * device for calls (see COMMUNICATIONS_DEVICE_ID), so a headset just
   * plugged in is the one recorded.
   */
  private async enableMic(deviceId?: string): Promise<boolean> {
    if (this.s.mic) return true
    const want = deviceId ?? this.prefs.mic
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: deviceId ? { exact: deviceId } : { ideal: want ?? COMMUNICATIONS_DEVICE_ID },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      patch(this.store, { mic: stream.getAudioTracks()[0] ?? null })
      return true
    } catch (err) {
      patch(this.store, { deviceError: describeDeviceError(err, 'microphone') })
      return false
    }
  }

  private disableMic(): void {
    this.s.mic?.stop()
    patch(this.store, { mic: null })
  }

  /** Devices change only between takes: the engine holds them while it records. */
  async toggleCamera(): Promise<void> {
    if (this.s.phase !== 'setup') return
    if (this.s.camera) this.disableCamera()
    else if (await this.enableCamera()) this.setBubble({ ...this.s.bubble, visible: true })
  }

  async toggleMic(): Promise<void> {
    if (this.s.phase !== 'setup') return
    if (this.s.mic) this.disableMic()
    else await this.enableMic()
  }

  /**
   * Record with this microphone, or with none (null). Between takes only.
   * The choice is remembered, and a call on this device starts with it too.
   * A device that will not start leaves the one before it in place.
   */
  async selectMic(deviceId: string | null): Promise<void> {
    if (this.s.phase !== 'setup') return
    patch(this.store, { deviceError: null })
    if (deviceId === null) {
      this.disableMic()
      return
    }
    if (deviceId === this.activeMicId.get()) return
    const before = this.s.mic
    patch(this.store, { mic: null })
    if (await this.enableMic(deviceId)) {
      before?.stop()
      this.remember('mic', deviceId)
    } else {
      patch(this.store, { mic: before })
    }
  }

  /** Record with this camera, or with none (null), as selectMic. */
  async selectCamera(deviceId: string | null): Promise<void> {
    if (this.s.phase !== 'setup') return
    patch(this.store, { deviceError: null })
    if (deviceId === null) {
      this.disableCamera()
      return
    }
    if (deviceId === this.activeCameraId.get()) return
    const before = this.s.camera
    patch(this.store, { camera: null })
    if (await this.enableCamera(deviceId)) {
      for (const track of before?.getTracks() ?? []) track.stop()
      this.remember('camera', deviceId)
      // A camera chosen is a camera to show (it had been let go if the bubble was hidden).
      if (!this.s.bubble.visible) this.setBubble({ ...this.s.bubble, visible: true })
    } else {
      patch(this.store, { camera: before })
    }
  }

  private remember(kind: keyof DevicePrefs, deviceId: string): void {
    this.prefs = { ...this.prefs, [kind]: deviceId }
    saveDevicePref(this.storage, kind, deviceId)
  }

  setMode(next: CaptureMode): void {
    if (this.s.phase !== 'setup' || this.s.mode === next) return
    patch(this.store, { mode: next })
  }

  setBubble(spec: BubbleSpec): void {
    patch(this.store, { bubble: spec })
    this.engine?.setBubble(spec)
  }

  /** The bubble to a corner of the frame. */
  snapBubble(corner: BubbleCorner): void {
    const { width, height } = this.frame.get()
    this.setBubble(bubbleAtCorner(this.s.bubble, corner, width, height))
  }

  /** One size up (1) or down (-1), kept inside the frame. */
  resizeBubble(dir: 1 | -1): void {
    const { width, height } = this.frame.get()
    this.setBubble(resizeBubble(this.s.bubble, dir, width, height))
  }

  /**
   * Show the camera bubble, or hide it and let the camera go: a bubble that is not shown has
   * no reason to keep the camera (and its light) on. Before a take that is the camera
   * stopped, or asked for again; during a take that paints the bubble the compositor is told
   * as well, and the camera comes back when the bubble does. A take that records the screen
   * as it is (the bubble was hidden when it began) has no bubble to bring back.
   */
  async setBubbleVisible(visible: boolean): Promise<void> {
    const { phase } = this.s
    if (phase !== 'setup' && phase !== 'recording' && phase !== 'paused') return
    if (this.s.mode !== 'screen') return
    const during = phase !== 'setup'
    if (during && !this.s.compositing) return
    if (!visible) {
      if (this.s.camera === null && !this.s.bubble.visible) return
      // Stop painting it, let the compositor go of the track, then stop the track itself.
      this.setBubble({ ...this.s.bubble, visible: false })
      if (during) this.engine?.setCamera(null)
      for (const track of this.s.camera?.getTracks() ?? []) track.stop()
      patch(this.store, { camera: null })
      return
    }
    if (this.s.camera === null) {
      patch(this.store, { deviceError: null })
      // A refusal leaves the bubble off, with the reason in deviceError.
      if (!(await this.enableCamera())) return
    }
    if (during) this.engine?.setCamera(this.s.camera?.getVideoTracks()[0] ?? null)
    this.setBubble({ ...this.s.bubble, visible: true })
  }

  /** The bubble on or off: as setBubbleVisible, which it returns. */
  toggleBubble(): Promise<void> {
    return this.setBubbleVisible(!this.s.bubble.visible)
  }

  /** Dragging: the bubble's centre to (x, y), fractions of the frame, kept inside it. */
  moveBubble(x: number, y: number): void {
    const { width, height } = this.frame.get()
    this.setBubble(moveBubbleTo(this.s.bubble, x, y, width, height))
  }

  /** (x, y), fractions of the frame, is on the bubble. */
  hitsBubble(x: number, y: number): boolean {
    const { width, height } = this.frame.get()
    return hitsBubble(this.s.bubble, x, y, width, height)
  }

  /** The longest a take may run, or null for no limit. Between takes only. */
  setLimit(ms: number | null): void {
    if (this.s.phase !== 'setup') return
    patch(this.store, { limitMs: ms !== null && ms > 0 ? ms : null })
  }

  setTitle(title: string): void {
    patch(this.store, { title })
  }

  // ------------------------------------------------------------- the take

  private sourcesNow(): CaptureSources {
    const s = this.s
    const onScreen = s.mode === 'screen'
    return {
      mode: s.mode,
      screen: onScreen ? (s.screen?.video ?? null) : null,
      camera: s.camera?.getVideoTracks()[0] ?? null,
      mic: s.mic,
      systemAudio: onScreen ? (s.screen?.audio ?? null) : null,
    }
  }

  private readonly hooks: CaptureHooks = {
    begin: async (mimeType) => {
      const trimmed = this.s.title.trim()
      const r = await this.videos.create({ mimeType, ...(trimmed ? { title: trimmed } : {}) })
      return {
        videoId: r.video.id,
        shareToken: r.video.shareToken,
        partBytes: r.partBytes,
        label: trimmed || 'Video',
      }
    },
    address: (videoId, partNumber) => this.videos.partAddress(videoId, partNumber),
    complete: (videoId, info) => this.videos.complete(videoId, info),
    abort: (videoId) => this.videos.abort(videoId),
    onProgress: (progress) => patch(this.store, { progress }),
  }

  /** Three, two, one, then the take. The composite exists only from here. */
  async start(): Promise<void> {
    if (this.s.phase !== 'setup' || !this.canStart.get() || !this.supported) return
    patch(this.store, { error: null, result: null, phase: 'countdown', countdown: 3 })
    await new Promise<void>((resolve) => {
      this.countdownTimer = setInterval(() => {
        if (this.s.phase === 'countdown') patch(this.store, { countdown: this.s.countdown - 1 })
        if (this.s.countdown <= 0 || this.s.phase !== 'countdown') {
          if (this.countdownTimer) clearInterval(this.countdownTimer)
          this.countdownTimer = null
          resolve()
        }
      }, 1000)
    })
    // Read again: the countdown may have been cancelled meanwhile.
    if (this.store.get().phase !== 'countdown') return
    // The count is over and its number is off the page (the page shows it only while it is above
    // zero); the take starts once that has been painted and the screen has had a moment to show
    // it gone, or the first frames of the recording are the last number of the count.
    await letThePageSettle()
    if (this.store.get().phase !== 'countdown') return
    let take: CaptureEngine | null = null
    try {
      take = new CaptureEngine(
        this.sourcesNow(),
        this.frame.get(),
        { ...this.s.bubble },
        this.hooks,
      )
      take.onStats = (health) => patch(this.store, { health })
      take.prepare()
      await take.start()
      this.engine = take
      patch(this.store, {
        codec: take.mimeType,
        compositing: take.compositing,
        phase: 'recording',
        elapsedMs: 0,
      })
      this.ticker = setInterval(() => {
        const elapsedMs = this.engine?.elapsedMs ?? this.s.elapsedMs
        patch(this.store, { elapsedMs })
        // At a limit the take is over, and saved like any other.
        const limit = this.s.limitMs
        if (limit !== null && elapsedMs >= limit) void this.stop()
      }, 250)
    } catch (err) {
      take?.dispose()
      patch(this.store, { error: `The take could not start: ${message(err)}`, phase: 'setup' })
    }
  }

  cancelCountdown(): void {
    if (this.s.phase !== 'countdown') return
    patch(this.store, { countdown: 0, phase: 'setup' })
  }

  pause(): void {
    if (this.s.phase !== 'recording') return
    this.engine?.pause()
    patch(this.store, { phase: 'paused' })
  }

  resume(): void {
    if (this.s.phase !== 'paused') return
    this.engine?.resume()
    patch(this.store, { phase: 'recording' })
  }

  private stopTicker(): void {
    if (this.ticker) clearInterval(this.ticker)
    this.ticker = null
  }

  /** Throw the take away and count down again over the same sources. */
  async restart(): Promise<void> {
    if (await this.discard()) void this.start()
  }

  /**
   * Throw the take away (nothing of it is kept) and go back to the setup,
   * with the screen, camera and microphone still chosen.
   */
  async discard(): Promise<boolean> {
    if (!this.engine || !this.running.get()) return false
    this.stopTicker()
    const old = this.engine
    this.engine = null
    patch(this.store, { phase: 'setup', elapsedMs: 0, progress: null })
    await old.discard().catch(() => undefined)
    old.dispose()
    return true
  }

  /** Stop, send the last parts, and hand back the result. */
  stop(): Promise<void> {
    if (this.pendingStop) return this.pendingStop
    if (!this.engine || !this.running.get()) return Promise.resolve()
    this.stopTicker()
    const current = this.engine
    patch(this.store, { phase: 'uploading' })
    this.pendingStop = (async () => {
      try {
        patch(this.store, { result: await current.stop(), phase: 'done' })
      } catch (err) {
        patch(this.store, {
          error: `The video could not be fully uploaded: ${message(err).replace(/\.$/, '')}. What was saved on this device can be sent from the Videos page.`,
          phase: 'error',
        })
      } finally {
        current.dispose()
        if (this.engine === current) this.engine = null
        patch(this.store, { health: null, compositing: false })
        this.pendingStop = null
      }
    })()
    return this.pendingStop
  }

  /** Back to the start, devices released. */
  release(): void {
    this.stopTicker()
    if (this.countdownTimer) clearInterval(this.countdownTimer)
    this.countdownTimer = null
    this.engine?.dispose()
    this.engine = null
    this.dropScreen()
    this.disableCamera()
    this.disableMic()
    patch(this.store, {
      phase: 'setup',
      countdown: 0,
      elapsedMs: 0,
      progress: null,
      error: null,
      deviceError: null,
      result: null,
      health: null,
      codec: null,
      compositing: false,
      title: '',
    })
  }

  /** The page is left. A take in progress is finished first; its upload outlives the page. */
  async leave(): Promise<void> {
    if (this.running.get()) await this.stop()
    else if (this.pendingStop) await this.pendingStop
    this.unfollow?.()
    this.unfollow = null
    this.release()
  }
}
